import { IS_PROD } from "../config/constants"

export const USER_TEXT_KEY = "write-code-user-text"
export const TEXT_CHANGED_EVENT = "write-code:text-changed"
export const OPEN_EDITOR_EVENT = "write-code:open-editor"

let cachedText: null | string = null
let cachedLines: null | readonly string[] = null

export function clearUserText(): void {
  localStorage.removeItem(USER_TEXT_KEY)
  resetSourceCache()
  window.dispatchEvent(new CustomEvent(TEXT_CHANGED_EVENT))
}

/** Current text the game would use — for pre-filling the editor modal. */
export function getCurrentSourceText(): string {
  return cachedText ?? readUserText() ?? ""
}

export function getSourceLines(): readonly string[] {
  if (cachedLines) return cachedLines
  const text = cachedText ?? readUserText() ?? ""
  cachedLines = text.length > 0 ? text.split("\n") : [""]
  return cachedLines
}

/**
 * Source resolution order:
 * 1. Local bundled file (write-code-source.txt) — when present and non-empty,
 *    locally it always wins over stored user text.
 * 2. User-provided text in localStorage — the fallback when no usable file
 *    exists (deployed: the file is never consulted).
 * 3. Empty string — no source at all; the app shows the empty notification.
 */
export async function loadSourceText(isProd = IS_PROD): Promise<string> {
  if (cachedText !== null) return cachedText
  if (!isProd) {
    try {
      const response = await fetch("/write-code-source.txt")
      if (response.ok) {
        const text = (await response.text()).replace(/\n$/, "")
        if (text.trim().length > 0) {
          cachedText = text
          cachedLines = null
          return cachedText
        }
      }
    } catch {
      // File unavailable — fall through to the user-provided text.
    }
  }
  const userText = readUserText()
  cachedText = userText ?? ""
  cachedLines = null
  return cachedText
}

export function openTextEditor(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EDITOR_EVENT))
}

export function readUserText(): null | string {
  try {
    const stored = localStorage.getItem(USER_TEXT_KEY)
    const trimmed = stored?.trim() ?? ""
    return trimmed.length > 0 ? trimmed : null
  } catch {
    return null
  }
}

export function resetSourceCache(): void {
  cachedText = null
  cachedLines = null
}

export function saveUserText(text: string): void {
  localStorage.setItem(USER_TEXT_KEY, text.trim())
  resetSourceCache()
  window.dispatchEvent(new CustomEvent(TEXT_CHANGED_EVENT))
}
